import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Link2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { getPO, getPOBilling, getReceipt, listPOs, listReceiptsForPO, listUnlinkedReceipts, saveInvoice, uploadInvoiceFile } from '../../api/purchase'
import { getAccounts, getControlAccounts, getVendors } from '../../api/master'
import { useToast } from '../../components/Toast.jsx'
import Button from '../../components/Button'
import Modal from '../../components/Modal'
import ErrorBanner from '../../components/ErrorBanner'
import FieldError from '../../components/FieldError'
import { BackLink, DocHeader } from '../../components/PageParts'
import { fmtDay, localToday } from '../../utils/format'
import { docNo } from '../../utils/docNumbers'
import { money, num } from '../sales/salesShared'
import { useSession } from '../../session'
import JobSelect from '../../components/JobSelect'
import { CreateOption } from '../../components/CreateOption'

const blankLine = (type) => (type === 'PO' ? { sku: '', name: '', qty: '1', unit_cost: '' } : { description: '', amount: '', account_code: '' })

// Enter a vendor's invoice. Product invoices follow a PO: the items fill in
// from the delivery (receipt) the invoice came with, with what's still left
// to bill on the order as a whole, since deliveries come in parts and a
// vendor may bill each part or the whole. Service invoices need only the
// vendor and go to Cost of Services unless another account is chosen. Overheads such
// as rent and utilities are entered under Expenses instead.
// Saved as a draft, then sent to Accounting from the invoice page.
export default function InvoiceForm() {
  const { has } = useSession()
  const books = has('accounting') // the plan has Accounting: show accounts and journal entries
  const nav = useNavigate()
  const [params] = useSearchParams()
  const { notify } = useToast()
  // Without Purchase (the Invoices plan) there are no orders: service invoices only.
  const products = has('purchase')
  const [type, setType] = useState(!products || params.get('type') === 'service' ? 'NonPO' : 'PO')
  // The plan arrives after the first render: switch to services once it does.
  useEffect(() => { if (!products) setType('NonPO') }, [products])
  const [pos, setPos] = useState([])
  const [poId, setPoId] = useState(params.get('po_id') || '')
  const [receipts, setReceipts] = useState([])
  const [receiptId, setReceiptId] = useState('')
  const [progress, setProgress] = useState({})
  const [vendors, setVendors] = useState([])
  const [vendorId, setVendorId] = useState('')
  const [jobId, setJobId] = useState(params.get('job') || '')
  const [accounts, setAccounts] = useState([])
  const [costOfServices, setCostOfServices] = useState('')
  const [invNumber, setInvNumber] = useState('')
  const [invDate, setInvDate] = useState(localToday())
  const [tax, setTax] = useState('')
  const [freight, setFreight] = useState('')
  const [discount, setDiscount] = useState('')
  const [file, setFile] = useState(null)
  const [rows, setRows] = useState([])
  const [mismatch, setMismatch] = useState('')
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkRows, setLinkRows] = useState([])
  const [saving, setSaving] = useState(false)
  const [formError, setFormError] = useState('')
  const [errors, setErrors] = useState({ header: {}, lines: {} })

  useEffect(() => {
    if (products) listPOs().then((r) => setPos(Array.isArray(r) ? r : [])).catch(() => setPos([]))
    getVendors().then((v) => setVendors(v || [])).catch(() => setVendors([]))
    getAccounts().then((a) => setAccounts(a || [])).catch(() => setAccounts([]))
    getControlAccounts().then((rows) => setCostOfServices((rows || []).find((r) => r.name === 'CostOfServices')?.account_code || '')).catch(() => {})
  }, [])

  // Product and service invoices have different lines; switching starts afresh.
  useEffect(() => {
    setRows(type === 'NonPO' ? [blankLine('NonPO')] : [])
    setErrors({ header: {}, lines: {} })
    setFormError('')
    setMismatch('')
  }, [type])

  // A PO's receipts, latest first selected.
  useEffect(() => {
    if (!poId) { setReceipts([]); setReceiptId(''); return }
    listReceiptsForPO(poId)
      .then((r) => { setReceipts(r || []); setReceiptId(r?.length ? String(r[0].receipt_id) : '') })
      .catch(() => { setReceipts([]); setReceiptId('') })
  }, [poId])

  // What the order as a whole has had received, returned and billed, by item code.
  const loadProgress = (id) => getPOBilling(parseInt(id, 10))
    .then((d) => setProgress(Object.fromEntries((d?.rows || []).map((r) => [String(r.sku), r]))))
    .catch(() => setProgress({}))
  useEffect(() => {
    if (!poId) { setProgress({}); return }
    loadProgress(poId)
  }, [poId])

  /** Items from the delivery (receipt) chosen, else from the order, each capped
   * at what's still left to bill on the order; priced as ordered. */
  const fillFromOrder = async () => {
    if (!poId) { setFormError('Choose the purchase order first.'); return }
    setMismatch('')
    try {
      const [po, billing] = await Promise.all([getPO(poId), getPOBilling(parseInt(poId, 10))])
      const poLines = (po?.lines || []).map((l) => ({ product_id: l.product_id, sku: l.sku, name: l.name, qty: num(l.quantity), unit_cost: num(l.unit_cost) }))
      const poCost = new Map(poLines.map((x) => [x.product_id, x.unit_cost]))
      const left = new Map((billing?.rows || []).map((r) => [r.product_id, r]))
      let source = poLines
      if (receiptId) {
        const rc = await getReceipt(receiptId).catch(() => null)
        const rLines = (rc?.lines || []).map((l) => ({ product_id: l.product_id, sku: l.sku, name: l.name, qty: num(l.quantity), unit_cost: num(l.unit_cost) }))
        if (rLines.length) source = rLines
      }
      const same = (a, b) => Math.abs(num(a) - num(b)) < 0.005
      const notes = []
      const use = []
      for (const l of source) {
        const b = left.get(l.product_id)
        const label = l.name || l.sku
        const toBill = b ? Math.max(0, num(b.to_bill)) : 0
        const qty = Math.min(num(l.qty), toBill)
        const cost = poCost.has(l.product_id) ? poCost.get(l.product_id) : num(l.unit_cost)
        if (!b || num(b.received) <= 0) { notes.push(`${label} hasn’t been received yet`); continue }
        if (qty <= 0) { notes.push(`${label}: everything received has been billed already`); continue }
        const net = num(b.received) - num(b.returned)
        if (net < num(b.ordered) - 0.005) notes.push(`${label}: ${net} of ${num(b.ordered)} ordered received so far`)
        if (num(b.billed) > 0) notes.push(`${label}: ${num(b.billed)} billed before, ${toBill} left`)
        if (receiptId && !same(l.unit_cost, cost)) notes.push(`${label}: received at ${money(l.unit_cost)}, ordered at ${money(cost)} (the order’s price is used)`)
        use.push({ sku: l.sku, name: l.name, qty: String(qty), unit_cost: cost.toFixed(2) })
      }
      setRows(use)
      if (!use.length) setFormError(source.length ? 'Everything received on this order has been billed already.' : 'Nothing has been received on this order yet.')
      else if (notes.length) setMismatch(`Part of the order: ${notes.join('; ')}.`)
    } catch (e) {
      setFormError(e.message || 'Could not load the order and its receipts.')
    }
  }

  // Fill the items as soon as a receipt is chosen, if none are entered yet.
  useEffect(() => {
    if (type === 'PO' && poId && receiptId && rows.length === 0) fillFromOrder()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId])

  const openLink = async () => {
    const po = pos.find((p) => String(p.po_id) === String(poId))
    if (!po?.vendor_id) { setFormError('Choose the purchase order first.'); return }
    try {
      setLinkRows((await listUnlinkedReceipts(po.vendor_id)) || [])
      setLinkOpen(true)
    } catch (e) {
      setFormError(e.message || 'Could not load the vendor’s other receipts.')
    }
  }

  const isPO = type === 'PO'
  const itemsTotal = isPO ? rows.reduce((s, r) => s + num(r.qty) * num(r.unit_cost), 0) : rows.reduce((s, r) => s + num(r.amount), 0)
  const total = itemsTotal + num(tax) + num(freight) - num(discount)
  const updateRow = (idx, patch) => setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)))
  const sortedPos = useMemo(() => [...pos].sort((a, b) => num(b.po_id) - num(a.po_id)), [pos])

  const save = async () => {
    setFormError('')
    const header = {}
    const lines = {}
    if (isPO) {
      if (!poId) header.po_id = 'Choose the purchase order.'
      if (!receiptId) header.receipt_id = 'Choose the receipt.'
      if (!file) header.file = 'Attach the vendor’s invoice.'
    } else if (!vendorId) header.vendor_id = 'Choose the vendor.'
    if (!invNumber.trim()) header.invoice_number = 'Enter the number on the vendor’s invoice.'
    if (!invDate) header.invoice_date = 'Enter the invoice date.'
    rows.forEach((r, i) => {
      const e = {}
      if (isPO) {
        if (!String(r.sku || '').trim()) e.sku = 'Enter the item code.'
        if (!(num(r.qty) > 0)) e.qty = 'Enter the quantity invoiced.'
        if (String(r.unit_cost).trim() === '' || Number.isNaN(Number(r.unit_cost)) || num(r.unit_cost) < 0) e.unit_cost = 'Enter the unit price.'
      } else {
        if (!String(r.description || '').trim()) e.description = 'Describe the charge.'
        if (!String(r.account_code || costOfServices || '').trim()) e.account_code = books ? 'Choose an account.' : 'Choose a category.'
        if (!(num(r.amount) > 0)) e.amount = 'Enter the amount.'
      }
      if (Object.keys(e).length) lines[i] = e
    })
    if (!rows.length) { setErrors({ header, lines }); setFormError(isPO ? 'Choose the PO and receipt so the items fill in.' : 'Add at least one charge.'); return }
    if (Object.keys(header).length || Object.keys(lines).length) {
      setErrors({ header, lines })
      setFormError('Please fix the highlighted fields.')
      return
    }
    setErrors({ header: {}, lines: {} })
    setSaving(true)
    try {
      let filePath
      if (file) {
        try {
          filePath = (await uploadInvoiceFile(file))?.file_path
        } catch {
          setFormError('The attachment didn’t upload. Please try again.')
          return
        }
      }
      const result = await saveInvoice({
        invoice_type: type,
        invoice_number: invNumber.trim(),
        invoice_date: invDate,
        tax_amount: num(tax),
        freight_amount: num(freight),
        discount_amount: num(discount),
        file_path: filePath,
        ...(isPO
          ? { po_id: parseInt(poId, 10), receipt_id: parseInt(receiptId, 10), lines: rows.map((r) => ({ sku: r.sku, quantity: num(r.qty), unit_price: num(r.unit_cost) })) }
          : { vendor_id: parseInt(vendorId, 10), job_id: jobId ? Number(jobId) : null, lines: rows.map((r) => ({ description: r.description, amount: num(r.amount), account_code: r.account_code || costOfServices })) }),
      })
      notify('Draft saved. Send it to Accounting when it’s ready.')
      nav(`/purchase/invoices/${result.invoice_id}`)
    } catch (e) {
      setFormError(e.message || 'Could not save the invoice.')
      if (e.fields) {
        const hdr = {}
        const lineErrs = {}
        for (const [k, v] of Object.entries(e.fields)) {
          const m = /^lines\[(\d+)\]\.([\w_]+)$/.exec(k)
          if (m) lineErrs[m[1]] = { ...(lineErrs[m[1]] || {}), [m[2]]: v }
          else hdr[k] = v
        }
        setErrors({ header: hdr, lines: lineErrs })
      }
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="ci-form-page">
      <DocHeader back={<BackLink to="/purchase/invoices" label="Vendor invoices" />} title="New vendor invoice" meta="Saved as a draft. Send it to Accounting from the next page when it’s right." />

      {formError && <ErrorBanner message={formError} />}

      {products && (
      <div className="segmented" role="radiogroup" aria-label="What is the invoice for?">
        <button type="button" role="radio" aria-checked={isPO} className={isPO ? 'active' : ''} onClick={() => setType('PO')}>
          Products<small>Follows a purchase order</small>
        </button>
        <button type="button" role="radio" aria-checked={!isPO} className={!isPO ? 'active' : ''} onClick={() => setType('NonPO')}>
          Services<small>Subcontractors and other job costs, no PO</small>
        </button>
      </div>
      )}

      <div className="ci-form-meta-card">
        <div className="ci-form-meta-top">
          {isPO ? (
            <>
              <label className="ci-form-field">
                <span>Purchase order</span>
                <select value={poId} onChange={(e) => { setPoId(e.target.value); setRows([]); setMismatch('') }}>
                  <option value="">Choose an order…</option>
                  {sortedPos.map((p) => <option key={p.po_id} value={p.po_id}>{docNo('po', p.po_no)} · {p.vendor_name || 'Vendor'}</option>)}
                </select>
                <FieldError error={errors.header.po_id} />
              </label>
              <label className="ci-form-field">
                <span>Delivery (receipt)</span>
                <select value={receiptId} onChange={(e) => { setReceiptId(e.target.value); setRows([]); setMismatch('') }}>
                  <option value="">{poId ? (receipts.length ? 'Choose a receipt…' : 'Nothing received yet') : 'Choose the order first'}</option>
                  {receipts.map((r) => <option key={r.receipt_id} value={r.receipt_id}>{docNo('receipt', r.receipt_no)}{r.receipt_date ? ` · ${fmtDay(r.receipt_date)}` : ''}{r.location_name ? ` · ${r.location_name}` : ''}</option>)}
                </select>
                <FieldError error={errors.header.receipt_id} />
              </label>
            </>
          ) : (
            <label className="ci-form-field">
              <span>Vendor</span>
              <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
                <option value="">Choose a vendor…</option>
                <CreateOption kind="vendor" />
                {[...vendors].sort((a, b) => String(a.name).localeCompare(String(b.name))).map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.name}</option>)}
              </select>
              <FieldError error={errors.header.vendor_id} />
            </label>
          )}
          {!isPO && <JobSelect value={jobId} onChange={setJobId} />}
          <label className="ci-form-field">
            <span>Vendor’s invoice number</span>
            <input value={invNumber} onChange={(e) => setInvNumber(e.target.value)} placeholder="As printed on their invoice" />
            <FieldError error={errors.header.invoice_number} />
          </label>
          <label className="ci-form-field">
            <span>Invoice date</span>
            <input type="date" value={invDate} onChange={(e) => setInvDate(e.target.value)} />
            <FieldError error={errors.header.invoice_date} />
          </label>
        </div>
        <div className="ci-form-meta-bottom">
          <label className="ci-form-field">
            <span>Vendor’s invoice{isPO ? '' : ' (optional)'}</span>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <FieldError error={errors.header.file} />
          </label>
        </div>
      </div>

      {mismatch && <p className="doc-note">{mismatch}</p>}

      <div className="ci-form-lines-card">
        <div className="ci-form-lines-head">
          <div className="ci-form-lines-title">{isPO ? 'Items' : 'Charges'}</div>
          {isPO && (
            <span className="row-actions">
              <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={fillFromOrder} disabled={!poId}><RefreshCw size={13} />Refill from PO</button>
              <button type="button" className="ci-btn ci-btn-ghost ci-btn-sm" onClick={openLink} disabled={!poId}><Link2 size={13} />Use another receipt</button>
            </span>
          )}
        </div>
        {isPO && rows.length === 0 ? (
          <p className="doc-empty">{poId ? (receiptId ? 'Loading the items…' : 'Choose the receipt to fill in the items.') : 'Choose the purchase order to fill in the items.'}</p>
        ) : (
          <div className="ci-form-table-wrap">
            <table className="ci-form-line-table compact lines-cards">
              <thead>
                {isPO
                  ? <tr><th>Item</th><th className="num col-qty">Qty</th><th className="num col-price">Unit price</th><th className="num col-amount">Amount</th><th className="col-del" /></tr>
                  : <tr><th>Description</th><th className="col-account">{books ? 'Account' : 'Category'}</th><th className="num col-price">Amount</th><th className="col-del" /></tr>}
              </thead>
              <tbody>
                {rows.map((r, i) => (isPO ? (
                  <tr key={i}>
                    <td data-label="Item code">
                      <input className="ci-form-cell-input" value={r.sku} placeholder="Item code" onChange={(e) => updateRow(i, { sku: e.target.value })} aria-label={`Item code ${i + 1}`} />
                      {(r.name || progress[String(r.sku).trim()]) && (
                        <span className="cell-sub cell-pad">
                          {r.name}
                          {progress[String(r.sku).trim()] && (() => {
                            const p = progress[String(r.sku).trim()]
                            return `${r.name ? ' · ' : ''}ordered ${num(p.ordered)} · received ${num(p.received) - num(p.returned)}${num(p.billed) ? ` · billed before ${num(p.billed)}` : ''} · left to bill ${Math.max(0, num(p.to_bill))}`
                          })()}
                        </span>
                      )}
                      <FieldError error={errors.lines?.[i]?.sku} />
                    </td>
                    <td data-label="Qty" className="lc-half">
                      <input className="ci-form-cell-input num" value={r.qty} inputMode="decimal" onChange={(e) => updateRow(i, { qty: e.target.value })} aria-label={`Quantity ${i + 1}`} />
                      <FieldError error={errors.lines?.[i]?.qty} />
                    </td>
                    <td data-label="Unit price" className="lc-half">
                      <input className="ci-form-cell-input num" value={r.unit_cost} inputMode="decimal" placeholder="0.00" onChange={(e) => updateRow(i, { unit_cost: e.target.value })} aria-label={`Unit price ${i + 1}`} />
                      <FieldError error={errors.lines?.[i]?.unit_cost} />
                    </td>
                    <td className="ci-form-cell-amount" data-label="Amount">{money(num(r.qty) * num(r.unit_cost))}</td>
                    <td className="ci-form-del-cell"><button type="button" className="ci-form-del-btn" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label={`Remove item ${i + 1}`}><Trash2 size={15} /></button></td>
                  </tr>
                ) : (
                  <tr key={i}>
                    <td data-label="Description">
                      <input className="ci-form-cell-input" value={r.description} placeholder="e.g. Subcontractor for the Smith job" onChange={(e) => updateRow(i, { description: e.target.value })} aria-label={`Description ${i + 1}`} />
                      <FieldError error={errors.lines?.[i]?.description} />
                    </td>
                    <td data-label="Account">
                      <select className="ci-form-cell-select" value={r.account_code || costOfServices} onChange={(e) => updateRow(i, { account_code: e.target.value })} aria-label={`Account ${i + 1}`}>
                        <option value="">{books ? 'Choose an account…' : 'Choose a category…'}</option>
                        <CreateOption kind="account" />
                        {accounts.map((a) => <option key={a.account_code} value={a.account_code}>{books ? `${a.account_code} · ${a.account_name}` : a.account_name}</option>)}
                      </select>
                      <FieldError error={errors.lines?.[i]?.account_code} />
                    </td>
                    <td data-label="Amount">
                      <input className="ci-form-cell-input num" value={r.amount} inputMode="decimal" placeholder="0.00" onChange={(e) => updateRow(i, { amount: e.target.value })} aria-label={`Amount ${i + 1}`} />
                      <FieldError error={errors.lines?.[i]?.amount} />
                    </td>
                    <td className="ci-form-del-cell"><button type="button" className="ci-form-del-btn" onClick={() => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : [blankLine('NonPO')]))} aria-label={`Remove charge ${i + 1}`}><Trash2 size={15} /></button></td>
                  </tr>
                )))}
              </tbody>
            </table>
          </div>
        )}
        {(!isPO || rows.length > 0) && (
          <div className="ci-form-add-row">
            <button type="button" className="ci-form-add-line" onClick={() => setRows((rs) => [...rs, blankLine(type)])}><Plus size={14} />{isPO ? 'Add item' : 'Add charge'}</button>
          </div>
        )}
      </div>

      <div className="ci-form-bottom-row">
        <div className="ci-form-meta-card extra-charges">
          <label className="ci-form-field"><span>Tax</span><input value={tax} inputMode="decimal" placeholder="0.00" onChange={(e) => setTax(e.target.value)} /></label>
          <label className="ci-form-field"><span>Freight and other charges</span><input value={freight} inputMode="decimal" placeholder="0.00" onChange={(e) => setFreight(e.target.value)} /></label>
          <label className="ci-form-field"><span>Discount</span><input value={discount} inputMode="decimal" placeholder="0.00" onChange={(e) => setDiscount(e.target.value)} /></label>
        </div>
        <div className="ci-form-totals">
          <div className="ci-form-total-row"><span>{isPO ? 'Items' : 'Charges'}</span><strong>{money(itemsTotal)}</strong></div>
          {num(tax) > 0 && <div className="ci-form-total-row"><span>Tax</span><strong>{money(tax)}</strong></div>}
          {num(freight) > 0 && <div className="ci-form-total-row"><span>Freight</span><strong>{money(freight)}</strong></div>}
          {num(discount) > 0 && <div className="ci-form-total-row"><span>Discount</span><strong>−{money(discount)}</strong></div>}
          <div className="ci-form-total-divider" />
          <div className="ci-form-grand-total"><span>Invoice total</span><strong>{money(total)}</strong></div>
        </div>
      </div>

      <div className="ci-form-action-bar">
        <div className="ci-form-action-left">
          <button type="button" className="ci-btn ci-btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save draft'}</button>
          <button type="button" className="ci-btn ci-btn-ghost" onClick={() => nav('/purchase/invoices')}>Cancel</button>
        </div>
      </div>

      <Modal
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        title="Use another receipt"
        subtitle={`Receipts from this vendor that aren’t linked to an invoice yet. The one you pick is used with ${docNo('po', pos.find((p) => String(p.po_id) === String(poId))?.po_no) || 'this order'}.`}
        footer={<Button variant="ghost" onClick={() => setLinkOpen(false)}>Close</Button>}
      >
        {linkRows.length ? (
          <table className="ci-line-table">
            <thead><tr><th>Receipt</th><th>Received</th><th>Location</th><th /></tr></thead>
            <tbody>
              {linkRows.map((r) => (
                <tr key={r.receipt_id}>
                  <td>{docNo('receipt', r.receipt_no)}</td>
                  <td>{fmtDay(r.receipt_date) || '–'}</td>
                  <td>{r.location_name || '–'}</td>
                  <td className="num"><Button variant="primary" size="sm" onClick={() => { setReceipts((rs) => (rs.some((x) => String(x.receipt_id) === String(r.receipt_id)) ? rs : [r, ...rs])); setReceiptId(String(r.receipt_id)); setRows([]); setLinkOpen(false) }}>Use this</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="doc-empty">This vendor has no other receipts waiting.</p>
        )}
      </Modal>
    </div>
  )
}
